import "./insights_setup.scss";
import { useContext, useEffect, useState } from "react";
import AppLifecycleContext from "@/lib/contexts/AppLifecycleContext";
import { ConditionItem } from "types/types.d";
import { InsightsService } from "@/lib/services/InsightsService";
import { NameSetup } from "../Components/Setup/NameSetup";
import { EventsSetup } from "../Components/Setup/EventsSetup";
import { useNavigate } from "react-router-dom";
import { LoadingComponent } from "@/ui/components/Common/Loading/Loading";
import { toast } from "react-toastify";
import { TOAST_MESSAGES } from "@/lib/utils/Constants";
import { InsightContext } from "@/lib/contexts/InsightContext";
import useInsightHook from "@/hooks/useInsightHook";
import { PowerUsersSelectRatio } from "../Components/Setup/PowerUsersSelectRatio";
import { ThirdPartyTracking } from "@/lib/utils/ThirdPartyTracking";
import { Button } from "@mantine/core";
import { SidePanel } from "@/ui/components/Common/SidePanel/SidePanel";
import useConditionsValidityHook from "@/hooks/useConditionsValidityHook";
import useInsightsListHook from "@/hooks/useInsightsListHook";

interface IPowerUsersSetupModal {
  isModalOpen: boolean;
  setIsModalOpen: (e: boolean) => void;
  insightId?: string;
}

const PowerUsersSetupModal = ({
  isModalOpen,
  setIsModalOpen,
  insightId
}: IPowerUsersSetupModal) => {
  // States that are used.
  const [isLoading, setIsLoading] = useState("idle");
  const [templateId, setTemplateId] = useState("");
  const [name, setName] = useState("");
  const [conditions, setConditions] = useState<Array<ConditionItem>>([]);
  const [powerUserRatio, setPowerUserRatio] = useState(5);

  const [isStoreLoader, setIsStoreLoader] = useState("idle");
  const navigate = useNavigate();
  const { stepsValidation } = useConditionsValidityHook();
  // We are passing attributes and events to the child components.
  const { insightEvents, insightAttributes } = useInsightHook();
  const { activeWorkspace } = useContext(AppLifecycleContext);
  const { insightsList } = useInsightsListHook("adoption_engagement");

  // NOTE: We are going to provide cohort filter in insights as discussed internally.

  // const [audienceConditions, setAudienceConditions] = useState<
  //   Array<ConditionItem>
  // >([]);
  // const [audienceConditionType, setAudienceConditionType] = useState<
  //   "or" | "and"
  // >("or");

  // all service calls
  const insightService = new InsightsService();
  const createInsight = async () => {
    setIsStoreLoader("loading");

    await insightService
      .create(activeWorkspace.id, {
        name: name,
        conditions: {
          power_user_conditions: conditions,
          power_user_ratio: powerUserRatio
        },
        type: "power_users"
      })
      .then((res) => {
        if (res.data) {
          toast.success(TOAST_MESSAGES.INSIGHT_CREATED);
          const TPService = new ThirdPartyTracking();
          TPService.track("power_users_insight_created");
          setIsModalOpen(false);
          // as the user is already on /env/${activeWorkspace.identifier}/insights/dashboard/adoption_engagement
          navigate({ search: `insightId=${res.data.id}` });
        }
      })
      .catch((err) => {});
    setIsStoreLoader("loaded");
  };

  const updateInsight = async () => {
    setIsStoreLoader("loading");

    await insightService
      .update(activeWorkspace.id, templateId, {
        name: name,
        conditions: {
          power_user_conditions: conditions,
          power_user_ratio: powerUserRatio
        }
      })
      .then((res) => {
        toast.success(TOAST_MESSAGES.INSIGHT_UPDATED);
        setIsModalOpen(false);
      })
      .catch((err) => {});
    setIsStoreLoader("loaded");
  };

  const getInsight = async () => {
    setIsLoading("loading");
    await insightService
      .get(activeWorkspace.id, insightId)
      .then((res) => {
        if (res.data) {
          setTemplateId(res.data.id);
          setName(res.data.name);
          setConditions(res.data.conditions.power_user_conditions);
          setPowerUserRatio(res.data.conditions.power_user_ratio);
        }
      })
      .catch((err) => {});
    setIsLoading("loaded");
  };

  useEffect(() => {
    if (insightId) {
      getInsight();
    } else {
      setIsLoading("loaded");
    }
  }, [insightId]);

  if (isLoading !== "loaded") {
    return (
      <>
        <LoadingComponent></LoadingComponent>
      </>
    );
  }

  const handleSubmission = async () => {
    if (name.length === 0) {
      toast.error("Please enter a name for your insight.");
      return;
    }

    // Check if the insight name already exists
    if (
      insightsList &&
      insightsList.find(
        (insight: any) =>
          insight.name.toLowerCase() === name.trim().toLowerCase() &&
          insight.id !== insightId &&
          insight.type === "power_users"
      )
    ) {
      toast.error("An insight with the same name already exists. Please enter a different name.");
      return;
    }

    const powerUserConditionsCheck = stepsValidation(conditions, 1, 10);
    if (!powerUserConditionsCheck) {
      return;
    }

    if (insightId) {
      await updateInsight();
    } else {
      await createInsight();
    }
  };

  return (
    <SidePanel
      opened={isModalOpen}
      onCancel={() => setIsModalOpen(false)}
      loading={true}
      title={"Power Users"}>
      <NameSetup
        name={name}
        setName={setName}
        placeholder={"e.g Power users who have published content..."}
      />
      <InsightContext.Provider
        value={{
          insightEvents,
          insightAttributes
        }}>
        <EventsSetup
          conditions={conditions}
          setConditions={setConditions}
          title={"Select events that make for a power user"}
          description={
            "You can select more than one event if you have multiple apps. e.g post published, task created."
          }
          conditionType={"or"}
        />
        <PowerUsersSelectRatio
          powerUserRatio={powerUserRatio}
          setPowerUserRatio={setPowerUserRatio}
        />
      </InsightContext.Provider>
      <div className="um-insights-setup__actions ">
        <Button
          onClick={() => {
            setIsModalOpen(false);
          }}
          color="red">
          Cancel
        </Button>
        {templateId ? (
          <>
            <Button
              onClick={() => {
                handleSubmission();
              }}
              loading={isStoreLoader === "loading"}
              disabled={conditions.length === 0 || isStoreLoader === "loading"}>
              Update Insight
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => {
                handleSubmission();
              }}
              loading={isStoreLoader === "loading"}
              disabled={conditions.length === 0 || isStoreLoader === "loading"}>
              Save Insight
            </Button>
          </>
        )}
      </div>
    </SidePanel>
  );
};

export default PowerUsersSetupModal;
